// Global Internationalization Service for Multi-Region PI-APP
// Following CODING_STANDARDS.md requirements for TypeScript strict mode and permanent solutions

import { EventEmitter } from 'events';
import { z } from 'zod';
import type { 
  SupportedRegion, 
  SupportedCurrency,
  SupportedTimezone 
} from '../types/regions';

// Zod schemas for input validation
const LocaleConfigSchema = z.object({
  code: z.string(),
  name: z.string(),
  nativeName: z.string(),
  region: z.enum(['US', 'UK', 'AU', 'IN', 'EU']),
  language: z.enum(['en', 'hi', 'de', 'fr', 'es', 'it', 'pt', 'nl']),
  country: z.string(),
  currency: z.enum(['USD', 'GBP', 'AUD', 'INR', 'EUR']),
  timezone: z.enum(['America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Australia/Sydney', 'Asia/Kolkata']),
  rtl: z.boolean(),
  dateFormat: z.string(),
  timeFormat: z.string(),
  numberFormat: z.object({
    decimal: z.string(),
    thousands: z.string(),
    grouping: z.array(z.number()),
    pattern: z.string()
  }),
  currencyFormat: z.object({
    symbol: z.string(),
    position: z.enum(['before', 'after']),
    spacing: z.boolean(),
    pattern: z.string()
  }),
  addressFormat: z.object({
    pattern: z.string(),
    postalCodePattern: z.string(),
    phonePattern: z.string()
  }),
  culturalSettings: z.object({
    formalityLevel: z.enum(['low', 'medium', 'high']),
    businessHours: z.object({
      start: z.string(),
      end: z.string(),
      workDays: z.array(z.number())
    }),
    holidays: z.array(z.string()),
    fiscalYearStart: z.string()
  })
});

const TranslationResourceSchema = z.object({
  locale: z.string(),
  namespace: z.string(),
  translations: z.record(z.string(), z.union([z.string(), z.record(z.string(), z.string())]))
});

export type LocaleConfig = z.infer<typeof LocaleConfigSchema>;
export type TranslationResource = z.infer<typeof TranslationResourceSchema>;

export interface TranslationContext {
  userId?: string;
  region?: SupportedRegion;
  domain?: 'finance' | 'insurance' | 'property' | 'general';
  formality?: 'formal' | 'informal';
  metadata?: Record<string, any>;
}

export interface FormatOptions {
  locale?: string;
  style?: 'decimal' | 'currency' | 'percent';
  currency?: SupportedCurrency;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
}

export interface AddressFormat {
  locale: string;
  lines: string[];
  postalCode: string;
  country: string;
  formatted: string;
}

export interface PhoneFormat {
  locale: string;
  countryCode: string;
  nationalNumber: string;
  international: string;
  formatted: string;
}

export class GlobalInternationalizationService extends EventEmitter {
  private static instance: GlobalInternationalizationService;
  private locales: Map<string, LocaleConfig> = new Map();
  private translations: Map<string, Map<string, any>> = new Map();
  private currentLocale: LocaleConfig;
  private fallbackLocale: string = 'en-US';
  private translationCache: Map<string, string> = new Map();
  private isInitialized = false;

  // Target region locales as specified in requirements
  private readonly TARGET_LOCALES: LocaleConfig[] = [
    {
      code: 'en-US',
      name: 'English (United States)',
      nativeName: 'English',
      region: 'US',
      language: 'en',
      country: 'US',
      currency: 'USD',
      timezone: 'America/New_York',
      rtl: false,
      dateFormat: 'MM/DD/YYYY',
      timeFormat: 'h:mm A',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        grouping: [3],
        pattern: '#,##0.##'
      },
      currencyFormat: {
        symbol: '$',
        position: 'before',
        spacing: false,
        pattern: '$#,##0.00'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{city}, {state} {zip}\n{country}',
        postalCodePattern: '\\d{5}(-\\d{4})?',
        phonePattern: '\\+?1?[-.\\s]?\\(?([0-9]{3})\\)?[-.\\s]?([0-9]{3})[-.\\s]?([0-9]{4})'
      },
      culturalSettings: {
        formalityLevel: 'medium',
        businessHours: { start: '09:00', end: '17:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['New Year', 'Independence Day', 'Thanksgiving', 'Christmas'],
        fiscalYearStart: '10-01'
      }
    },
    {
      code: 'en-GB',
      name: 'English (United Kingdom)',
      nativeName: 'English',
      region: 'UK',
      language: 'en',
      country: 'GB',
      currency: 'GBP',
      timezone: 'Europe/London',
      rtl: false,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: 'HH:mm',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        grouping: [3],
        pattern: '#,##0.##'
      },
      currencyFormat: {
        symbol: '£',
        position: 'before',
        spacing: false,
        pattern: '£#,##0.00'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{city}\n{county}\n{postcode}\n{country}',
        postalCodePattern: '[A-Z]{1,2}[0-9][A-Z0-9]? [0-9][A-Z]{2}',
        phonePattern: '\\+?44[-.\\s]?\\(?([0-9]{4})\\)?[-.\\s]?([0-9]{6})'
      },
      culturalSettings: {
        formalityLevel: 'high',
        businessHours: { start: '09:00', end: '17:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['New Year', 'Easter', 'Christmas', 'Boxing Day'],
        fiscalYearStart: '04-06'
      }
    },
    {
      code: 'en-AU',
      name: 'English (Australia)',
      nativeName: 'English',
      region: 'AU',
      language: 'en',
      country: 'AU',
      currency: 'AUD',
      timezone: 'Australia/Sydney',
      rtl: false,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: 'h:mm A',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        grouping: [3],
        pattern: '#,##0.##'
      },
      currencyFormat: {
        symbol: '$',
        position: 'before',
        spacing: false,
        pattern: '$#,##0.00'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{suburb} {state} {postcode}\n{country}',
        postalCodePattern: '\\d{4}',
        phonePattern: '\\+?61[-.\\s]?\\(?([0-9]{1})\\)?[-.\\s]?([0-9]{4})[-.\\s]?([0-9]{4})'
      },
      culturalSettings: {
        formalityLevel: 'medium',
        businessHours: { start: '09:00', end: '17:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['New Year', 'Australia Day', 'ANZAC Day', 'Christmas'],
        fiscalYearStart: '07-01'
      }
    },
    {
      code: 'hi-IN',
      name: 'Hindi (India)',
      nativeName: 'हिन्दी',
      region: 'IN',
      language: 'hi',
      country: 'IN',
      currency: 'INR',
      timezone: 'Asia/Kolkata',
      rtl: false,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: 'h:mm A',
      numberFormat: {
        decimal: '.',
        thousands: ',',
        grouping: [3, 2],
        pattern: '#,##,##0.##'
      },
      currencyFormat: {
        symbol: '₹',
        position: 'before',
        spacing: true,
        pattern: '₹ #,##,##0.00'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{city} - {pincode}\n{state}\n{country}',
        postalCodePattern: '\\d{6}',
        phonePattern: '\\+?91[-.\\s]?([0-9]{5})[-.\\s]?([0-9]{5})'
      },
      culturalSettings: {
        formalityLevel: 'high',
        businessHours: { start: '09:30', end: '17:30', workDays: [1, 2, 3, 4, 5] },
        holidays: ['Diwali', 'Holi', 'Independence Day', 'Gandhi Jayanti'],
        fiscalYearStart: '04-01'
      }
    },
    {
      code: 'de-DE',
      name: 'German (Germany)',
      nativeName: 'Deutsch',
      region: 'EU',
      language: 'de',
      country: 'DE',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      rtl: false,
      dateFormat: 'DD.MM.YYYY',
      timeFormat: 'HH:mm',
      numberFormat: {
        decimal: ',',
        thousands: '.',
        grouping: [3],
        pattern: '#.##0,##'
      },
      currencyFormat: {
        symbol: '€',
        position: 'after',
        spacing: true,
        pattern: '#.##0,00 €'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{postcode} {city}\n{country}',
        postalCodePattern: '\\d{5}',
        phonePattern: '\\+?49[-.\\s]?\\(?([0-9]{3,4})\\)?[-.\\s]?([0-9]{7,8})'
      },
      culturalSettings: {
        formalityLevel: 'high',
        businessHours: { start: '08:00', end: '17:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['Neujahr', 'Ostern', 'Tag der Arbeit', 'Weihnachten'],
        fiscalYearStart: '01-01'
      }
    },
    {
      code: 'fr-FR',
      name: 'French (France)',
      nativeName: 'Français',
      region: 'EU',
      language: 'fr',
      country: 'FR',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      rtl: false,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: 'HH:mm',
      numberFormat: {
        decimal: ',',
        thousands: ' ',
        grouping: [3],
        pattern: '# ##0,##'
      },
      currencyFormat: {
        symbol: '€',
        position: 'after',
        spacing: true,
        pattern: '# ##0,00 €'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{postcode} {city}\n{country}',
        postalCodePattern: '\\d{5}',
        phonePattern: '\\+?33[-.\\s]?\\(?([0-9]{1})\\)?[-.\\s]?([0-9]{2})[-.\\s]?([0-9]{2})[-.\\s]?([0-9]{2})[-.\\s]?([0-9]{2})'
      },
      culturalSettings: {
        formalityLevel: 'high',
        businessHours: { start: '09:00', end: '17:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['Nouvel An', 'Pâques', 'Fête du Travail', 'Noël'],
        fiscalYearStart: '01-01'
      }
    },
    {
      code: 'es-ES',
      name: 'Spanish (Spain)',
      nativeName: 'Español',
      region: 'EU',
      language: 'es',
      country: 'ES',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      rtl: false,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: 'HH:mm',
      numberFormat: {
        decimal: ',',
        thousands: '.',
        grouping: [3],
        pattern: '#.##0,##'
      },
      currencyFormat: {
        symbol: '€',
        position: 'after',
        spacing: true,
        pattern: '#.##0,00 €'
      },
      addressFormat: {
        pattern: '{name}\n{street}\n{postcode} {city}\n{country}',
        postalCodePattern: '\\d{5}',
        phonePattern: '\\+?34[-.\\s]?([0-9]{3})[-.\\s]?([0-9]{3})[-.\\s]?([0-9]{3})'
      },
      culturalSettings: {
        formalityLevel: 'medium',
        businessHours: { start: '09:00', end: '18:00', workDays: [1, 2, 3, 4, 5] },
        holidays: ['Año Nuevo', 'Semana Santa', 'Día del Trabajo', 'Navidad'],
        fiscalYearStart: '01-01'
      }
    }
  ];

  private constructor() {
    super();
    
    // Set max listeners to prevent memory leak warnings
    this.setMaxListeners(20);
    
    this.initializeTargetLocales();
    this.currentLocale = this.TARGET_LOCALES[0]; // Default to en-US
    
    // Ensure EventEmitter methods are available
    this.off = this.removeListener.bind(this);
  }

  public static getInstance(): GlobalInternationalizationService {
    if (!GlobalInternationalizationService.instance) {
      GlobalInternationalizationService.instance = new GlobalInternationalizationService();
    }
    return GlobalInternationalizationService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Load all translation resources
      await this.loadTranslationResources();
      
      // Initialize user preferences
      await this.loadUserPreferences();
      
      this.isInitialized = true;
      this.emit('initialized');
    } catch (error) {
      console.error('Failed to initialize GlobalInternationalizationService:', error);
      throw new Error('Failed to initialize GlobalInternationalizationService');
    }
  }

  /**
   * Set current locale and update all formatting
   */
  public async setLocale(localeCode: string, context?: TranslationContext): Promise<void> {
    try {
      const locale = this.locales.get(localeCode);
      if (!locale) {
        throw new Error(`Unsupported locale: ${localeCode}`);
      }

      // Ensure we have a current locale before proceeding
      if (!this.currentLocale) {
        this.currentLocale = this.TARGET_LOCALES[0]; // Default to en-US
      }

      const previousLocale = this.currentLocale;
      this.currentLocale = locale;

      // Update document attributes
      this.updateDocumentLocale();
      
      // Clear translation cache for new locale
      this.translationCache.clear();
      
      // Save user preference
      if (context?.userId) {
        await this.saveUserLocalePreference(context.userId, localeCode);
      }

      this.emit('locale:changed', {
        previous: previousLocale || null,
        current: this.currentLocale || null,
        context: context || {}
      });
    } catch (error) {
      throw new Error(`Failed to set locale: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get current locale configuration
   */
  public getCurrentLocale(): LocaleConfig {
    // Ensure we always return a valid locale
    if (!this.currentLocale) {
      this.currentLocale = this.TARGET_LOCALES[0]; // Default to en-US
    }
    return this.currentLocale;
  }

  /**
   * Get all supported locales
   */
  public getSupportedLocales(): LocaleConfig[] {
    return Array.from(this.locales.values());
  }

  /**
   * Translate text with context
   */
  public translate(key: string, context?: TranslationContext): string {
    const cacheKey = `${this.currentLocale.code}:${key}:${JSON.stringify(context || {})}`;
    const cached = this.translationCache.get(cacheKey);
    if (cached) return cached;

    try {
      const translation = this.getTranslation(key, context);
      this.translationCache.set(cacheKey, translation);
      return translation;
    } catch (error) {
      console.warn(`Translation missing for key: ${key}`, error);
      return key; // Return key as fallback
    }
  }

  /**
   * Translate with pluralization
   */
  public translatePlural(key: string, count: number, context?: TranslationContext): string {
    const pluralKey = this.getPluralKey(key, count);
    return this.translate(pluralKey, context);
  }

  /**
   * Format number according to locale
   */
  public formatNumber(value: number, options?: FormatOptions): string {
    const locale = options?.locale || this.currentLocale.code;
    const numberFormat = this.currentLocale.numberFormat;

    return new Intl.NumberFormat(locale, {
      style: options?.style || 'decimal',
      currency: options?.currency || this.currentLocale.currency,
      minimumFractionDigits: options?.minimumFractionDigits,
      maximumFractionDigits: options?.maximumFractionDigits,
      useGrouping: options?.useGrouping !== false
    }).format(value);
  }

  /**
   * Format currency according to locale
   */
  public formatCurrency(value: number, currency?: SupportedCurrency): string {
    const currencyCode = currency || this.currentLocale.currency;
    
    return new Intl.NumberFormat(this.currentLocale.code, {
      style: 'currency',
      currency: currencyCode
    }).format(value);
  }

  /**
   * Format date according to locale
   */
  public formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(this.currentLocale.code, {
      timeZone: this.currentLocale.timezone,
      ...options
    }).format(date);
  }

  /**
   * Format time according to locale
   */
  public formatTime(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      timeZone: this.currentLocale.timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: this.currentLocale.timeFormat.includes('A')
    };

    return new Intl.DateTimeFormat(this.currentLocale.code, options).format(date);
  }

  /**
   * Format address according to locale
   */
  public formatAddress(address: {
    name?: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  }): AddressFormat {
    const pattern = this.currentLocale.addressFormat.pattern;
    const formatted = pattern.replace(/\{(\w+)\}/g, (match, field) => {
      return address[field as keyof typeof address] || '';
    }).replace(/\n\s*\n/g, '\n').trim();

    return {
      locale: this.currentLocale.code,
      lines: formatted.split('\n').filter(line => line.trim()),
      postalCode: address.postcode || '',
      country: address.country || this.currentLocale.country,
      formatted
    };
  }

  /**
   * Format phone number according to locale
   */
  public formatPhoneNumber(phone: string, international: boolean = false): PhoneFormat {
    // This is a simplified implementation - in production would use libphonenumber
    const cleaned = phone.replace(/\D/g, '');
    const countryCode = this.getCountryCodeForLocale();
    
    let nationalNumber = cleaned;
    if (cleaned.startsWith(countryCode)) {
      nationalNumber = cleaned.substring(countryCode.length);
    }

    const formatted = this.applyPhoneFormatPattern(nationalNumber);
    const internationalFormat = international ? `+${countryCode} ${formatted}` : formatted;

    return {
      locale: this.currentLocale.code,
      countryCode,
      nationalNumber,
      international: internationalFormat,
      formatted
    };
  }

  /**
   * Get region-specific business information
   */
  public getBusinessInfo(): {
    hours: { start: string; end: string; workDays: number[] };
    holidays: string[];
    fiscalYear: string;
    formality: string;
  } {
    return {
      hours: this.currentLocale.culturalSettings.businessHours,
      holidays: this.currentLocale.culturalSettings.holidays,
      fiscalYear: this.currentLocale.culturalSettings.fiscalYearStart,
      formality: this.currentLocale.culturalSettings.formalityLevel
    };
  }

  /**
   * Get financial terminology for region
   */
  public getFinancialTerminology(): Record<string, string> {
    // Return region-specific financial terms
    const baseTerms = {
      'account': this.translate('finance.account'),
      'balance': this.translate('finance.balance'),
      'transaction': this.translate('finance.transaction'),
      'transfer': this.translate('finance.transfer'),
      'savings': this.translate('finance.savings'),
      'checking': this.translate('finance.checking'),
      'investment': this.translate('finance.investment'),
      'loan': this.translate('finance.loan'),
      'mortgage': this.translate('finance.mortgage'),
      'insurance': this.translate('finance.insurance')
    };

    return baseTerms;
  }

  /**
   * Get insurance terminology for region
   */
  public getInsuranceTerminology(): Record<string, string> {
    return {
      'policy': this.translate('insurance.policy'),
      'premium': this.translate('insurance.premium'),
      'claim': this.translate('insurance.claim'),
      'coverage': this.translate('insurance.coverage'),
      'deductible': this.translate('insurance.deductible'),
      'beneficiary': this.translate('insurance.beneficiary')
    };
  }

  /**
   * Get property terminology for region
   */
  public getPropertyTerminology(): Record<string, string> {
    return {
      'property': this.translate('property.property'),
      'mortgage': this.translate('property.mortgage'),
      'rent': this.translate('property.rent'),
      'lease': this.translate('property.lease'),
      'landlord': this.translate('property.landlord'),
      'tenant': this.translate('property.tenant')
    };
  }

  /**
   * Detect user's preferred locale
   */
  public detectUserLocale(): string {
    if (typeof navigator !== 'undefined') {
      const languages = navigator.languages || [navigator.language];
      
      for (const language of languages) {
        const exactMatch = this.locales.get(language);
        if (exactMatch) return language;
        
        // Try language without country code
        const languageCode = language.split('-')[0];
        const partialMatch = Array.from(this.locales.values()).find(
          locale => locale.language === languageCode
        );
        if (partialMatch) return partialMatch.code;
      }
    }
    
    return this.fallbackLocale;
  }

  /**
   * Initialize target locales
   */
  private initializeTargetLocales(): void {
    this.TARGET_LOCALES.forEach(locale => {
      this.locales.set(locale.code, locale);
    });
  }

  /**
   * Load translation resources for all locales
   */
  private async loadTranslationResources(): Promise<void> {
    for (const locale of this.TARGET_LOCALES) {
      try {
        const translations = await this.loadLocaleTranslations(locale.code);
        this.translations.set(locale.code, translations);
      } catch (error) {
        console.warn(`Failed to load translations for ${locale.code}:`, error);
      }
    }
  }

  /**
   * Load translations for a specific locale
   */
  private async loadLocaleTranslations(localeCode: string): Promise<Map<string, any>> {
    // In production, this would load from translation files or API
    const translations = new Map<string, any>();
    
    // Load common translations
    translations.set('common', await this.getCommonTranslations(localeCode));
    translations.set('finance', await this.getFinanceTranslations(localeCode));
    translations.set('insurance', await this.getInsuranceTranslations(localeCode));
    translations.set('property', await this.getPropertyTranslations(localeCode));
    
    return translations;
  }

  /**
   * Get translation for a key
   */
  private getTranslation(key: string, context?: TranslationContext): string {
    const [namespace, ...keyParts] = key.split('.');
    const translationKey = keyParts.join('.');
    
    const localeTranslations = this.translations.get(this.currentLocale.code);
    const fallbackTranslations = this.translations.get(this.fallbackLocale);
    
    const namespaceTranslations = localeTranslations?.get(namespace) || 
                                 fallbackTranslations?.get(namespace);
    
    return this.getNestedValue(namespaceTranslations, translationKey) || key;
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): string | null {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  /**
   * Get plural key based on count and locale rules
   */
  private getPluralKey(key: string, count: number): string {
    // Simplified plural rules - in production would use Intl.PluralRules
    let pluralForm = 'other';
    
    if (count === 0) pluralForm = 'zero';
    else if (count === 1) pluralForm = 'one';
    else if (count === 2) pluralForm = 'two';
    
    return `${key}.${pluralForm}`;
  }

  /**
   * Update document locale attributes
   */
  private updateDocumentLocale(): void {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = this.currentLocale.code;
      document.documentElement.dir = this.currentLocale.rtl ? 'rtl' : 'ltr';
    }
  }

  /**
   * Load user locale preferences
   */
  private async loadUserPreferences(): Promise<void> {
    // In production, load from user preferences database
  }

  /**
   * Save user locale preference
   */
  private async saveUserLocalePreference(userId: string, localeCode: string): Promise<void> {
    // In production, save to user preferences database
  }

  /**
   * Get country code for current locale
   */
  private getCountryCodeForLocale(): string {
    const countryCodes: Record<string, string> = {
      'US': '1',
      'GB': '44', 
      'AU': '61',
      'IN': '91',
      'DE': '49',
      'FR': '33',
      'ES': '34'
    };
    
    return countryCodes[this.currentLocale.country] || '1';
  }

  /**
   * Apply phone format pattern
   */
  private applyPhoneFormatPattern(number: string): string {
    // Simplified formatting - in production would use proper phone formatting
    if (number.length === 10) {
      return `(${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
    }
    return number;
  }

  // Translation loading methods (mock data for demonstration)
  private async getCommonTranslations(locale: string): Promise<Record<string, string>> {
    const translations: Record<string, Record<string, string>> = {
      'en-US': {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        selectLanguage: 'Select Language',
        languageDescription: 'Choose your preferred language for the interface',
        currentLanguage: 'Current Language'
      },
      'en-GB': {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success'
      },
      'en-AU': {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success'
      },
      'hi-IN': {
        save: 'सेव करें',
        cancel: 'रद्द करें',
        delete: 'डिलीट करें',
        edit: 'एडिट करें',
        loading: 'लोड हो रहा है...',
        error: 'एरर',
        success: 'सफलता',
        selectLanguage: 'भाषा चुनें',
        languageDescription: 'इंटरफ़ेस के लिए अपनी पसंदीदा भाषा चुनें',
        currentLanguage: 'वर्तमान भाषा'
      },
      'de-DE': {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        loading: 'Laden...',
        error: 'Fehler',
        success: 'Erfolg',
        selectLanguage: 'Sprache auswählen',
        languageDescription: 'Wählen Sie Ihre bevorzugte Sprache für die Benutzeroberfläche',
        currentLanguage: 'Aktuelle Sprache'
      },
      'fr-FR': {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        loading: 'Chargement...',
        error: 'Erreur',
        success: 'Succès',
        selectLanguage: 'Choisir la langue',
        languageDescription: 'Choisissez votre langue préférée pour l\'interface',
        currentLanguage: 'Langue actuelle'
      },
      'es-ES': {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        loading: 'Cargando...',
        error: 'Error',
        success: 'Éxito',
        selectLanguage: 'Seleccionar idioma',
        languageDescription: 'Elija su idioma preferido para la interfaz',
        currentLanguage: 'Idioma actual'
      }
    };

    return translations[locale] || translations['en-US'];
  }

  private async getFinanceTranslations(locale: string): Promise<Record<string, string>> {
    const translations: Record<string, Record<string, string>> = {
      'en-US': {
        account: 'Account',
        balance: 'Balance',
        transaction: 'Transaction',
        transfer: 'Transfer',
        savings: 'Savings Account',
        checking: 'Checking Account',
        investment: 'Investment',
        loan: 'Loan',
        mortgage: 'Mortgage',
        insurance: 'Insurance'
      },
      'hi-IN': {
        account: 'खाता',
        balance: 'शेष राशि',
        transaction: 'लेनदेन',
        transfer: 'ट्रांसफर',
        savings: 'बचत खाता',
        checking: 'चालू खाता',
        investment: 'निवेश',
        loan: 'ऋण',
        mortgage: 'गिरवी',
        insurance: 'बीमा'
      },
      'de-DE': {
        account: 'Konto',
        balance: 'Saldo',
        transaction: 'Transaktion',
        transfer: 'Übertragung',
        savings: 'Sparkonto',
        checking: 'Girokonto',
        investment: 'Investition',
        loan: 'Darlehen',
        mortgage: 'Hypothek',
        insurance: 'Versicherung'
      },
      'fr-FR': {
        account: 'Compte',
        balance: 'Solde',
        transaction: 'Transaction',
        transfer: 'Transfert',
        savings: 'Compte Épargne',
        checking: 'Compte Courant',
        investment: 'Investissement',
        loan: 'Prêt',
        mortgage: 'Hypothèque',
        insurance: 'Assurance'
      },
      'es-ES': {
        account: 'Cuenta',
        balance: 'Saldo',
        transaction: 'Transacción',
        transfer: 'Transferencia',
        savings: 'Cuenta de Ahorros',
        checking: 'Cuenta Corriente',
        investment: 'Inversión',
        loan: 'Préstamo',
        mortgage: 'Hipoteca',
        insurance: 'Seguro'
      }
    };

    return translations[locale] || translations['en-US'];
  }

  private async getInsuranceTranslations(locale: string): Promise<Record<string, string>> {
    const translations: Record<string, Record<string, string>> = {
      'en-US': {
        policy: 'Policy',
        premium: 'Premium',
        claim: 'Claim',
        coverage: 'Coverage',
        deductible: 'Deductible',
        beneficiary: 'Beneficiary'
      },
      'hi-IN': {
        policy: 'पॉलिसी',
        premium: 'प्रीमियम',
        claim: 'दावा',
        coverage: 'कवरेज',
        deductible: 'कटौती योग्य',
        beneficiary: 'लाभार्थी'
      },
      'de-DE': {
        policy: 'Police',
        premium: 'Prämie',
        claim: 'Anspruch',
        coverage: 'Deckung',
        deductible: 'Selbstbehalt',
        beneficiary: 'Begünstigter'
      },
      'fr-FR': {
        policy: 'Police',
        premium: 'Prime',
        claim: 'Réclamation',
        coverage: 'Couverture',
        deductible: 'Franchise',
        beneficiary: 'Bénéficiaire'
      },
      'es-ES': {
        policy: 'Póliza',
        premium: 'Prima',
        claim: 'Reclamación',
        coverage: 'Cobertura',
        deductible: 'Deducible',
        beneficiary: 'Beneficiario'
      }
    };

    return translations[locale] || translations['en-US'];
  }

  private async getPropertyTranslations(locale: string): Promise<Record<string, string>> {
    const translations: Record<string, Record<string, string>> = {
      'en-US': {
        property: 'Property',
        mortgage: 'Mortgage',
        rent: 'Rent',
        lease: 'Lease',
        landlord: 'Landlord',
        tenant: 'Tenant'
      },
      'hi-IN': {
        property: 'संपत्ति',
        mortgage: 'गिरवी',
        rent: 'किराया',
        lease: 'लीज',
        landlord: 'मकान मालिक',
        tenant: 'किरायेदार'
      },
      'de-DE': {
        property: 'Eigentum',
        mortgage: 'Hypothek',
        rent: 'Miete',
        lease: 'Mietvertrag',
        landlord: 'Vermieter',
        tenant: 'Mieter'
      },
      'fr-FR': {
        property: 'Propriété',
        mortgage: 'Hypothèque',
        rent: 'Loyer',
        lease: 'Bail',
        landlord: 'Propriétaire',
        tenant: 'Locataire'
      },
      'es-ES': {
        property: 'Propiedad',
        mortgage: 'Hipoteca',
        rent: 'Alquiler',
        lease: 'Arrendamiento',
        landlord: 'Arrendador',
        tenant: 'Inquilino'
      }
    };

    return translations[locale] || translations['en-US'];
  }

  /**
   * Remove event listener (alias for removeListener)
   */
  public off(event: string | symbol, listener: (...args: any[]) => void): this {
    return this.removeListener(event, listener);
  }

  public async shutdown(): Promise<void> {
    this.isInitialized = false;
    this.emit('shutdown');
  }
}

// Export singleton instance
export const globalI18nService = GlobalInternationalizationService.getInstance();